/**
 * Decodes (does NOT verify) a JWT, ported from the `jwt-decode` package. Returns
 * the payload, or the header when `{header: true}` is passed. Throws
 * InvalidTokenError on malformed input, matching the package's behaviour.
 *
 * This performs no signature verification — never trust the output for auth
 * decisions, exactly as with jwt-decode.
 */

export interface JwtHeader {
  typ?: string;
  alg?: string;
  kid?: string;
}

export interface JwtPayload {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  exp?: number;
  nbf?: number;
  iat?: number;
  jti?: string;
}

export interface JwtDecodeOptions {
  header?: boolean;
}

export class InvalidTokenError extends Error {}
InvalidTokenError.prototype.name = 'InvalidTokenError';

function b64DecodeUnicode(str: string): string {
  return decodeURIComponent(
    atob(str).replace(/(.)/g, (_match, char: string) => {
      const code = char.charCodeAt(0).toString(16).toUpperCase();
      return `%${code.length < 2 ? `0${code}` : code}`;
    })
  );
}

function base64UrlDecode(str: string): string {
  let output = str.replace(/-/g, '+').replace(/_/g, '/');
  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += '==';
      break;
    case 3:
      output += '=';
      break;
    default:
      throw new Error('base64 string is not of the correct length');
  }

  try {
    return b64DecodeUnicode(output);
  } catch {
    return atob(output);
  }
}

export function jwtDecode<T = JwtPayload>(token: string, options: JwtDecodeOptions = {}): T {
  if (typeof token !== 'string') {
    throw new InvalidTokenError('Invalid token specified: must be a string');
  }

  const pos = options.header === true ? 0 : 1;
  const part = token.split('.')[pos];

  if (typeof part !== 'string') {
    throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
  }

  let decoded: string;
  try {
    decoded = base64UrlDecode(part);
  } catch (e) {
    throw new InvalidTokenError(
      `Invalid token specified: invalid base64 for part #${pos + 1} (${(e as Error).message})`
    );
  }

  try {
    return JSON.parse(decoded) as T;
  } catch (e) {
    throw new InvalidTokenError(
      `Invalid token specified: invalid json for part #${pos + 1} (${(e as Error).message})`
    );
  }
}
